/** @format */

import {ChangeEvent, useEffect, useState} from 'react';

import Alert from '@mui/material/Alert';
import {Box, Grid2 as Grid, Typography} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {makeStyles, createStyles} from '@mui/styles';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import MediumTextField from 'components/inputs/MediumTextField';
import ModalLoading from 'components/loading/ModalLoading';
import ModalPage from 'components/ModalPage';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import SecondaryMediumButton from 'components/buttons/SecondaryMediumButton';
import {useSearchParams} from 'helpers/routerHelpers';

import {
  useCreateSpecialistMutation,
  useDeleteSpecialistMutation,
  useGetCurrentUserQuery,
  useUpdateSpecialistMutation,
} from '../graphql/__generated__';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    hint: {
      color: '#8C8D8D',
      lineHeight: '18px',
    },
  }),
);

interface StateType {
  name: string;
  email: string;
}

export const SpecialistForm = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const searchParams = useSearchParams();
  const id = searchParams.get('id');

  const {t} = useTranslation();

  const [state, setState] = useState<StateType>({name: '', email: ''});
  const [saveSpecialistError, setSaveSpecialistError] = useState('');

  const {loading, error, data} = useGetCurrentUserQuery({
    variables: {id},
  });
  const [createSpecialist, {loading: createSpecialistLoading}] = useCreateSpecialistMutation();
  const [updateSpecialist, {loading: updateSpecialistLoading}] = useUpdateSpecialistMutation();
  const [deleteSpecialist, {loading: deleteSpecialistLoading}] = useDeleteSpecialistMutation();

  useEffect(() => {
    if (!data?.currentUser?.specialist) return;

    setState({
      name: data.currentUser.specialist.name,
      email: data.currentUser.specialist.email ?? '',
    });
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser) {
    return <ModalLoading title='Пожалуйста, подождите' />;
  }

  const {specialist} = data.currentUser;

  const renderForm = () => {
    const handleChange = (event: ChangeEvent<{name: string; value: string}>) => {
      const {name, value} = event.target;
      setState({...state, [name]: value});
    };

    return (
      <Box mt={6}>
        <Grid container spacing={6}>
          <Grid size={{xs: 12}}>
            <MediumTextField
              error={Boolean(saveSpecialistError)}
              placeholder={t('Имя специалиста', 'Имя специалиста')}
              value={state.name}
              slotProps={{
                htmlInput: {
                  name: 'name',
                },
              }}
              myProps={{placeholderFocused: ''}}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{xs: 12}}>
            <MediumTextField
              error={Boolean(saveSpecialistError)}
              placeholder={t('E-mail', 'E-mail')}
              value={state.email}
              slotProps={{
                htmlInput: {
                  name: 'email',
                },
              }}
              myProps={{placeholderFocused: ''}}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 10}}>
            <Typography variant='h2'>
              <Trans>Уведомления в Telegram</Trans>
            </Typography>
            <Box mt={3}>
              <Typography variant='body2' className={classes.hint}>
                <Trans>
                  Ссылка для подлючения бота будет доступна в настройках уведомлений для
                  пользователя после его создания.
                </Trans>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderButton = () => {
    const disabled =
      !state.name || createSpecialistLoading || updateSpecialistLoading || deleteSpecialistLoading;

    const handleSave = async () => {
      try {
        if (specialist) {
          await updateSpecialist({
            variables: {
              id: specialist.id,
              name: state.name,
              email: state.email,
            },
          });
        } else {
          await createSpecialist({
            variables: {name: state.name, email: state.email},
          });
        }

        navigate(-1);
      } catch (e) {
        setSaveSpecialistError((e as Error).message);
      }
    };

    const handleDelete = async () => {
      if (!specialist) return;

      await deleteSpecialist({variables: {id: specialist.id}});
      navigate('/home/settings/specialists', {replace: true});
    };

    return (
      <Box mt={10}>
        <Grid container spacing={4}>
          <Grid size={{xs: 12, sm: 6}}>
            <PrimaryMediumButton disabled={disabled} onClick={handleSave}>
              <Trans>Сохранить</Trans>
            </PrimaryMediumButton>
          </Grid>

          {specialist && specialist.notifications.length === 0 && (
            <Grid size={{xs: 12, sm: 6}}>
              <SecondaryMediumButton disabled={disabled} onClick={handleDelete}>
                <Trans>Удалить</Trans>
              </SecondaryMediumButton>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const header = () => {
    if (specialist) {
      return t('Редактирование специалиста', 'Редактирование специалиста');
    }

    return t('Создание специалиста', 'Создание специалиста');
  };

  return (
    <ModalPage header={header()}>
      {renderForm()}
      {renderButton()}
    </ModalPage>
  );
};
